import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { IoMdArrowDropdown } from "react-icons/io"
import { AiOutlineLoading3Quarters } from "react-icons/ai"
import styles from "./form.module.css"
import Checkbox from './Checkbox'
import Input from "./Input"

const Select = ({ name, placeholder, multiple = false, showOptionAll = true, options = [], selected = "", emptyName, theme, required = false, allowDeselectAll = true, disabled = false, loading = false, loadingText = "Carregando opções...", emptyOptionsText = "Nenhuma opção encontrada", onChange = () => { } }) => {
    const [textSearch, setTextSearch] = useState("")
    const [active, setActive] = useState(false)
    const [stylesSelect, setStylesSelect] = useState({})

    const selectRef = useRef()
    const safeOptions = useMemo(() => {
        return (Array.isArray(options) ? options : []).filter(option => option && option.id !== undefined && option.id !== null)
    }, [options])
    const selectedValues = useMemo(() => Array.isArray(selected) ? selected : [], [selected])

    useEffect(() => {
        if (multiple) {
            if (!selected) {
                handleToogleAll()
            }
        } else {
            if (Array.isArray(selected)) {
                onChange("")
            }
        }
    }, [])

    const getOptionsByValue = () => {
        if (loading) {
            return loadingText
        }

        if (selected || selected?.toString()) {
            if (multiple) {
                const optionsSelected = safeOptions?.filter(option => selectedValues.includes(option.id))
                if (!optionsSelected?.length) {
                    return emptyName || name
                }
                return `${name} (${optionsSelected?.length || 0})`
            } else {
                let selectedText = ''
                safeOptions?.forEach(option => {
                    if (option.id?.toString() === selected?.toString()) {
                        selectedText = option.value
                    }
                })
                return selectedText ? selectedText : name
            }
        } else {
            return name
        }
    }

    const setSelectedOption = (id, only = false) => {
        if (loading) return

        if (multiple) {
            if (only) {
                onChange([id])
            } else {
                const index = selectedValues.indexOf(id)
                if (index !== -1) {
                    const newValue = [...selectedValues]
                    newValue.splice(index, 1)
                    onChange(newValue)
                } else {
                    const newValue = [...selectedValues]
                    newValue.push(id)
                    onChange(newValue)
                }
            }
        } else {
            if (allowDeselectAll && id?.toString() === selected) {
                onChange()
            } else {
                onChange(id?.toString())
                setActive(false)
                setTextSearch("")
            }
        }
    }

    const handleTextSearch = (text) => {
        setTextSearch(text)
    }

    const handleToogleAll = () => {
        if (loading) return

        let newValue = []
        if (selectedValues.length !== safeOptions.length) {
            newValue = safeOptions?.map(option => option.id)
        }
        onChange(newValue)
    }

    const updateDropdownPosition = () => {
        if (!selectRef.current) return

        const viewportPadding = 8
        const dropdownGap = 8
        const windowHeight = window.innerHeight
        const windowWidth = window.innerWidth
        const details = selectRef.current.getBoundingClientRect()
        const spaceBelow = windowHeight - details.bottom - viewportPadding
        const spaceAbove = details.top - viewportPadding
        const preferredDropdownHeight = 400
        const setToUp = spaceBelow < preferredDropdownHeight && spaceAbove > spaceBelow
        const availableHeight = Math.max((setToUp ? spaceAbove : spaceBelow) - dropdownGap, 0)
        const maxHeight = Math.min(preferredDropdownHeight, availableHeight)

        const longestOptionLength = Math.max(
            `${name || ""}`.length,
            `${placeholder || ""}`.length,
            ...safeOptions.map(option => `${option?.value || ""}`.length)
        )
        const estimatedContentWidth = (longestOptionLength * 7.5) + (multiple ? 124 : 72)
        const availableWidth = Math.max(windowWidth - (viewportPadding * 2), 0)
        const dropdownWidth = Math.min(
            Math.max(details.width, Math.min(Math.max(estimatedContentWidth, 320), 560)),
            availableWidth
        )

        const canAlignLeft = details.left + dropdownWidth <= windowWidth - viewportPadding
        const canAlignRight = details.right - dropdownWidth >= viewportPadding
        let left = details.left
        if (!canAlignLeft && canAlignRight) {
            left = details.right - dropdownWidth
        } else if (!canAlignLeft) {
            left = Math.min(Math.max(details.left, viewportPadding), windowWidth - dropdownWidth - viewportPadding)
        }

        setStylesSelect({
            top: setToUp ? `${details.top - dropdownGap}px` : `${details.bottom + dropdownGap}px`,
            left: `${Math.max(left, viewportPadding)}px`,
            width: `${dropdownWidth}px`,
            maxWidth: `calc(100vw - ${viewportPadding * 2}px)`,
            maxHeight: `${maxHeight}px`,
            transform: setToUp ? "translateY(-100%)" : "",
        })
    }

    useLayoutEffect(() => {
        if (!active) return undefined

        updateDropdownPosition()
        const handleViewportChange = () => updateDropdownPosition()
        window.addEventListener("resize", handleViewportChange)
        window.addEventListener("scroll", handleViewportChange, true)

        return () => {
            window.removeEventListener("resize", handleViewportChange)
            window.removeEventListener("scroll", handleViewportChange, true)
        }
    }, [active, safeOptions, multiple, name, placeholder])

    const handleToogleVisibleOptions = () => {
        if (disabled && !loading) {
            setActive(false)
        } else if (active) {
            setTextSearch("")
            setActive(false)
        } else {
            updateDropdownPosition()
            setActive(true)
        }
    }

    const renderOptions = () => {
        if (!active || typeof document === "undefined") return null

        return createPortal(
            <div className={`${styles.select} ${styles.selectPortal}`}>
                <div className={styles.backgroundForm} onClick={handleToogleVisibleOptions} />
                <div className={styles.groupOptions} style={stylesSelect}>
                    {!loading &&
                        <div className={styles.search}>
                            <Input type="search" placeholder={placeholder} value={textSearch} onChange={(e) => handleTextSearch(e.target.value)} autoFocus={true} />
                        </div>
                    }
                    <div className={styles.options}>
                        {loading &&
                            <div className={styles.loadingOption}>
                                <AiOutlineLoading3Quarters />
                                <span>{loadingText}</span>
                            </div>
                        }
                        {!loading && multiple && showOptionAll &&
                            <div className={styles.selectAll} onClick={handleToogleAll}>
                                <Checkbox checked={selectedValues.length === safeOptions.length} />
                                <span>Todos</span>
                            </div>
                        }
                        {!loading && safeOptions && safeOptions.map(option => (
                            (!textSearch || (`${option?.value || ""}`.toLowerCase().indexOf(textSearch?.toLowerCase())) !== -1) &&
                            <div key={option.id} className={styles.option} onClick={() => setSelectedOption(option.id)}>
                                <Checkbox checked={multiple ? selectedValues.indexOf(option.id) !== -1 : selected?.toString() === option.id?.toString()} />
                                <span title={typeof option.value === "string" ? option.value : undefined}>{option.value}</span>
                                {multiple &&
                                    <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); setSelectedOption(option.id, true) }}>Somente</button>
                                }
                            </div>
                        ))}
                        {!loading && safeOptions.length === 0 &&
                            <div className={styles.emptyOption}>{emptyOptionsText}</div>
                        }
                    </div>
                </div>
            </div>,
            document.body
        )
    }

    return (
        <>
            <div className={`${styles.select} ${active ? styles.active : ''} ${styles[theme]} ${disabled ? styles.disabled : ''} ${loading ? styles.loading : ''}`} ref={selectRef} tabIndex={1}>
                <div className={styles.text} onClick={handleToogleVisibleOptions} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleToogleVisibleOptions()} tabIndex="0" role="button">
                    <span>
                        {getOptionsByValue()}
                    </span>
                    {loading ? <AiOutlineLoading3Quarters className={styles.loadingIcon} /> : <IoMdArrowDropdown />}
                </div>
            </div>
            {renderOptions()}
        </>
    )
}

export default Select
